import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCheckbox,
  IonCol,
  IonGrid,
  IonIcon,
  IonInput,
  IonItem,
  IonItemGroup,
  IonLabel,
  IonList,
  IonRadio,
  IonRadioGroup,
  IonRow,
  IonSpinner,
  IonTextarea,
} from '@ionic/angular/standalone';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { DndDirective } from 'src/app/core/directives/dnd/dnd.directive';
import { DuplicatedValueValidatorDirective } from 'src/app/core/directives/dnd/dnd.directive.spec';
import { UppercaseDirective } from 'src/app/core/directives/uppercase/uppercase.directive';
import { AdminRoutingModule } from '../admin/admin.routing.module';
import { SecurityScreeningFormComponent } from '../position-operations/clear-security-screening/security-screening-form/security-screening-form.component';
import { PackageInterceptConfirmationModalComponent } from '../revenue-recovery/package-intercept-confirmation-modal/package-intercept-confirmation-modal.component';
import { DeleteFileConfirmationPopupComponent } from './delete-file-confirmation-popup/delete-file-confirmation-popup.component';
import { FileUploadResultsComponent } from './file-upload-results/file-upload-results.component';
import { FileUploadComponent } from './file-upload/file-upload.component';
import { ActionButtonComponent } from './form/components/action-button/action-button.component';
import { DatePickerComponent } from './form/components/date-picker/date-picker.component';
import { FormCheckboxComponent } from './form/components/form-checkbox/form-checkbox.component';
import { FormInputFieldComponent } from './form/components/form-input-field/form-input-field.component';
import { FormMultiselectDropdownComponent } from './form/components/form-multiselect-dropdown/form-multiselect-dropdown.component';
import { FormSelectComponent } from './form/components/form-select/form-select.component';
import { FormTextFieldComponent } from './form/components/form-text-field/form-text-field.component';
import { FormTimePickerComponent } from './form/components/form-time-picker/form-time-picker.component';
import { NoResultsFoundComponent } from './form/components/no-results-found/no-results-found.component';
import { ReadOnlyFieldsComponent } from './form/components/read-only-fields/read-only-fields.component';
import { ColumnFormComponent } from './form/two-column-form/column-form/column-form.component';
import { TwoColumnFormComponent } from './form/two-column-form/two-column-form.component';
import { LoadingModalComponent } from './modal/components/loading-modal/loading-modal.component';
import { ModalFrameComponent } from './modal/components/modal-frame/modal-frame.component';
import { ExitModalComponent } from './modal/exit-modal/exit-modal.component';
import { KeyEntryModalComponent } from './modal/key-entry-modal/key-entry-modal.component';
import { PackageStatusDetailsComponent } from './modal/package-status-details/package-status-details.component';
import { ProfileModalComponent } from './modal/profile-modal/profile-modal.component';
import { FileSizePipe } from './pipes/file-size.pipe';
import { FilterByCodePipe } from './pipes/filter-by-code.pipe';
import { ActiveCarryOverLoadsTableComponent } from './table/active-carry-over-loads-table/active-carry-over-loads-table.component';
import { ClearSecurityScreeningTableComponent } from './table/clear-security-screening-table/clear-security-screening-table.component';
import { AddNewScanComponent } from './table/components/add-new-scan/add-new-scan.component';
import { FileUploadButtonComponent } from './table/components/add-new-scan/file-upload-button/file-upload-button.component';
import { TableActionCellComponent } from './table/components/table-action-cell/table-action-cell.component';
import { TableDropdownStatusComponent } from './table/components/table-dropdown-status/table-dropdown-status.component';
import { TableHeaderDropdownComponent } from './table/components/table-header-dropdown/table-header-dropdown.component';
import { ExcelIconComponent } from './table/components/table-header/excel-icon/excel-icon.component';
import { FilterIconComponent } from './table/components/table-header/filter-icon/filter-icon.component';
import { HeaderSearchComponent } from './table/components/table-header/header-search/header-search.component';
import { NewItemIconComponent } from './table/components/table-header/new-item-icon/new-item-icon.component';
import { RadioButtonComponent } from './table/components/table-header/radio-button/radio-button.component';
import { TableHeaderComponent } from './table/components/table-header/table-header.component';
import { TablePaginationComponent } from './table/components/table-pagination/table-pagination.component';
import { TableSearchComponent } from './table/components/table-search-component/table-search.component';
import { TableSortingBoxComponent } from './table/components/table-sorting-box/table-sorting-box.component';
import { TableTimePickerComponent } from './table/components/table-time-picker/table-time-picker.component';
import { TableTrackingNumberCellComponent } from './table/components/table-tracking-number-cell/table-tracking-number-cell.component';
import { EndOutboundScanTableComponent } from './table/end-outbound-scan-table/end-outbound-scan-table.component';
import { HandlingScanTableComponent } from './table/handling-scan-table/handling-scan-table.component';
import { LocationAdminTableComponent } from './table/location-admin-table/location-admin-table.component';
import { OutboundScanExpandedRowComponent } from './table/outbound-scan-table/outbound-scan-expanded-row/outbound-scan-expanded-row.component';
import { OutboundScanTableComponent } from './table/outbound-scan-table/outbound-scan-table.component';
import { ShipperConfigTableComponent } from './table/shipper-config-table/shipper-config-table.component';
import { SizeAuditTableComponent } from './table/size-audit-table/size-audit-table.component';
import { UserAdminTableComponent } from './table/user-admin-table/user-admin-table.component';
import { WarehouseOutboundTableComponent } from './table/warehouse-outbound-table/warehouse-outbound-table.component';
import { WarehouseScanTableComponent } from './table/warehouse-scan-table/warehouse-scan-table.component';
import { WorldWideExpressFreightTableComponent } from './table/world-wide-express-freight-table/world-wide-express-freight-table.component';
import { TabsComponent } from './tabs/tabs.component';
import { DeviceAdminTableComponent } from './table/device-admin-table/device-admin-table.component';

@NgModule({
  declarations: [
    FilterIconComponent,
    TableHeaderComponent,
    TabsComponent,
    TableSortingBoxComponent,
    FormCheckboxComponent,
    UserAdminTableComponent,
    LocationAdminTableComponent,
    DeviceAdminTableComponent,
    DuplicatedValueValidatorDirective,
    TwoColumnFormComponent,
    ColumnFormComponent,
    FormMultiselectDropdownComponent,
    TableDropdownStatusComponent,
    ActionButtonComponent,
    TableTimePickerComponent,
    ExcelIconComponent,
    RadioButtonComponent,
    LoadingModalComponent,
    ProfileModalComponent,
    ReadOnlyFieldsComponent,
    OutboundScanTableComponent,
    OutboundScanExpandedRowComponent,
    ExitModalComponent,
    FileUploadComponent,
    EndOutboundScanTableComponent,
    ClearSecurityScreeningTableComponent,
    HandlingScanTableComponent,
    WarehouseScanTableComponent,
    WarehouseOutboundTableComponent,
    SizeAuditTableComponent,
    WorldWideExpressFreightTableComponent,
    PackageStatusDetailsComponent,
    KeyEntryModalComponent,
    ExitModalComponent,
    DndDirective,
    ActiveCarryOverLoadsTableComponent,
    SecurityScreeningFormComponent,
    FileUploadResultsComponent,
    FileSizePipe,
    DeleteFileConfirmationPopupComponent,
    ShipperConfigTableComponent,
  ],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatSortModule,
    MatCheckboxModule,
    AdminRoutingModule,
    MatIconModule,
    CommonModule,
    MatTableModule,
    NgSelectModule,
    HeaderSearchComponent,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    NgxMaterialTimepickerModule,
    MatTooltipModule,
    IonCard,
    IonRow,
    IonIcon,
    IonButton,
    IonItem,
    IonCheckbox,
    IonLabel,
    IonInput,
    IonItemGroup,
    IonCardContent,
    IonList,
    IonSpinner,
    IonGrid,
    IonCol,
    IonTextarea,
    IonRadio,
    IonRow,
    NoResultsFoundComponent,
    TableActionCellComponent,
    NewItemIconComponent,
    ModalFrameComponent,
    FormInputFieldComponent,
    DatePickerComponent,
    FormSelectComponent,
    TableSearchComponent,
    TableHeaderDropdownComponent,
    AddNewScanComponent,
    IonRadioGroup,
    FileUploadButtonComponent,
    FormTextFieldComponent,
    FormTimePickerComponent,
    TableTrackingNumberCellComponent,
    TablePaginationComponent,
    UppercaseDirective,
    FilterByCodePipe,
    PackageInterceptConfirmationModalComponent,
  ],
  exports: [
    CommonModule,
    FilterIconComponent,
    TableHeaderComponent,
    TabsComponent,
    TableSearchComponent,
    TableSortingBoxComponent,
    FormCheckboxComponent,
    FormSelectComponent,
    FormTextFieldComponent,
    DuplicatedValueValidatorDirective,
    TwoColumnFormComponent,
    ColumnFormComponent,
    NewItemIconComponent,
    NoResultsFoundComponent,
    FormMultiselectDropdownComponent,
    ActionButtonComponent,
    TableTimePickerComponent,
    FileUploadResultsComponent,
    TableHeaderDropdownComponent,
    PackageStatusDetailsComponent,
    TableTrackingNumberCellComponent,
    ExcelIconComponent,
    TableDropdownStatusComponent,
    RadioButtonComponent,
    FormTimePickerComponent,
    LocationAdminTableComponent,
    DeviceAdminTableComponent,
    UserAdminTableComponent,
    LoadingModalComponent,
    IonGrid,
    ReadOnlyFieldsComponent,
    OutboundScanTableComponent,
    FileUploadComponent,
    EndOutboundScanTableComponent,
    ClearSecurityScreeningTableComponent,
    HandlingScanTableComponent,
    ExitModalComponent,
    WarehouseScanTableComponent,
    WarehouseOutboundTableComponent,
    AddNewScanComponent,
    SizeAuditTableComponent,
    WorldWideExpressFreightTableComponent,
    ExitModalComponent,
    DndDirective,
    ActiveCarryOverLoadsTableComponent,
    SecurityScreeningFormComponent,
    ModalFrameComponent,
    FormInputFieldComponent,
    DatePickerComponent,
    MatTableModule,
    IonRadioGroup,
    FileUploadButtonComponent,
    FormTextFieldComponent,
    FileSizePipe,
    DeleteFileConfirmationPopupComponent,
    TablePaginationComponent,
    ShipperConfigTableComponent,
    UppercaseDirective,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SharedModule {}
